import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { L10nContext } from 'context/L10nContext';

import { AsyncReducerStatus } from 'store/common/AsyncReducerStatus';
import { useTypedSelector } from 'store/common/TypedSelector';

import { DeviceViewModel } from 'services/device/domain/model/DeviceModel';
import { SequenceCreateFailedNotification } from 'services/device/presentation/ui/sequence-action/sequence-create-modal/SequenceCreateFailedNotification';
import { selectCreatedSequence } from 'services/device/store/sequenceSlice';

import { ModalDialogueHeader } from 'presentation/ui/compositions/modal-dialogue/modal-dialogue-header/ModalDialogueHeader';
import { ModalDialogueSection } from 'presentation/ui/compositions/modal-dialogue/modal-dialogue-section/ModalDialogueSection';
import { ModalDialogue } from 'presentation/ui/compositions/modal-dialogue/ModalDialogue';
import { IconIdentifier } from 'presentation/ui/partials/icon/IconIdentifier';
import { MeasurementGoalCreateForm } from 'services/device/presentation/ui/measurement-goal-action/measurement-goal-create-form/MeasurementGoalCreateForm';
import { MeasurementGoalCreateSuccessNotification } from 'services/device/presentation/ui/measurement-goal-action/measurement-goal-create-modal/MeasurementGoalCreateSuccessNotification';
import { MeasurementGoalCreateFailedNotification } from 'services/device/presentation/ui/measurement-goal-action/measurement-goal-create-modal/MeasurementGoalCreateFailedNotification';

interface MeasurementGoalCreateModalProps {
	device: DeviceViewModel;
	onDismiss: () => void;
}

/**
 * The device create modal
 */
export const MeasurementGoalCreateModal = (props: MeasurementGoalCreateModalProps): JSX.Element => {
	const { device, onDismiss } = props;

	// Consume the context
	const l10nContext = useContext(L10nContext);

	// Use state to hold error objects
	const [caughtError, setCaughtError] = useState<Error>(null);

	// Use selector to provide information from store
	const createdSequence = useSelector(selectCreatedSequence());
	const sequenceActionStatus = useTypedSelector(state => state.sequences.actionStatus);

	const renderModal = () => {
		if (caughtError !== null) {
			return (
				<SequenceCreateFailedNotification
					onClickConfirm={onDismiss}
				/>
			);
		}

		let modal: JSX.Element = null;

		switch (sequenceActionStatus) {
			case AsyncReducerStatus.IDLE:
				modal = <MeasurementGoalCreateForm
					device={device}
					onError={(error) => {
						setCaughtError(error);
					}}
				/>;
				break;

			case AsyncReducerStatus.CREATED:
				modal =
					<MeasurementGoalCreateSuccessNotification
						onClickConfirm={onDismiss}
						sequenceCreated={createdSequence}
					/>;
				break;

			case AsyncReducerStatus.FAILED:
				modal =
					<MeasurementGoalCreateFailedNotification
						onClickConfirm={onDismiss}
					/>;
				break;
		}
		return modal;
	};

	return (
		<ModalDialogue onDismiss={onDismiss}>
			<ModalDialogueHeader
				captionText={l10nContext.translate('view.clearances.modal.sequence.create.title', 'Mitarbeiter/Messziel hinzufügen')}
				captionIcon={IconIdentifier.ADD}
			/>
			<ModalDialogueSection>
				{renderModal()}
			</ModalDialogueSection>
		</ModalDialogue>
	);
};
